/**
 * RUI-06: the prospect note / call note contract.
 *
 * - A note typed in the contact panel is a PROSPECT note: it is kept in
 *   ProspectQueue.notes, across every call to that contact.
 * - When a call to that contact is live (the panel's liveCallId), the same
 *   note is also appended to that call's `call_notes`. Before, the panel never
 *   wrote `call_notes`, so the AI analysis (which needs call_notes) never ran
 *   for calls qualified from the panel.
 * - The qualification reads the call's stored `call_notes` when its payload
 *   carries none (DialerContext.qualifyCallMutation).
 */
import { api } from '@/api/api-client';

/**
 * Texts the app itself writes into call_notes when it ends a call (Stop,
 * timeouts, a line taken by another prospect, launch failures). They are not
 * the agent's notes: the AI analysis, the CRM sync and the meeting
 * notification must not receive them, and an agent note replaces them.
 */
const SYSTEM_NOTE_PATTERNS = [
  /^Arr[eê]t (manuel|automatique)/i,
  /^Raccroch/i,
  /^Pas de r[eé]ponse apr[eè]s \d+\s?s$/i,
  /^Appel automatiquement termin[eé]/i,
  /^Echec lancement:/i,
  /^Batch skip/i,
  / pris en premier( \(r[eé]pondeur ici\))?$/i,
  / est en ligne$/i,
];

export function isSystemCallNote(text: string | null | undefined): boolean {
  const t = (text ?? '').trim();
  return !!t && SYSTEM_NOTE_PATTERNS.some((re) => re.test(t));
}

export interface ProspectNote {
  text: string;
  timestamp: string;
  pinned: boolean;
}

/** `call_notes` after adding `text`: one note per line, newest last. */
export function appendCallNote(existing: string | null | undefined, text: string): string {
  const note = text.trim();
  if (!note) return existing ?? '';
  if (!existing || !existing.trim() || isSystemCallNote(existing)) return note;
  return `${existing.trimEnd()}\n${note}`;
}

/** Notes the qualification hands to the AI analysis and the CRM sync. */
export function qualificationNotes(payloadNotes: string | null | undefined, storedCallNotes: string | null | undefined): string {
  if (payloadNotes && payloadNotes.trim()) return payloadNotes;
  return isSystemCallNote(storedCallNotes) ? '' : (storedCallNotes ?? '');
}

/**
 * Saves a prospect note, and mirrors it to the live call's `call_notes` when
 * there is one. Returns the serialized prospect notes.
 */
export async function saveProspectNote(input: {
  prospectId: string;
  previous: ProspectNote[];
  text: string;
  liveCallId?: string | null;
  liveCallNotes?: string | null;
  now?: Date;
}): Promise<string> {
  const note: ProspectNote = { text: input.text.trim(), timestamp: (input.now ?? new Date()).toISOString(), pinned: false };
  const serialized = JSON.stringify([note, ...input.previous]);
  await api.entities.ProspectQueue.update(input.prospectId, { notes: serialized });
  if (input.liveCallId) {
    // Read the call's current notes: the polled snapshot can miss a note added
    // a moment ago, and appending to it would erase that note.
    let current = input.liveCallNotes ?? '';
    try {
      const fresh = (await api.entities.ParallelCall.get(input.liveCallId)) as { call_notes?: unknown } | null;
      if (fresh && typeof fresh.call_notes === 'string') current = fresh.call_notes;
    } catch { /* fall back to the snapshot */ }
    await api.entities.ParallelCall.update(input.liveCallId, { call_notes: appendCallNote(current, note.text) });
  }
  return serialized;
}
